// Agency Agents - Agent roster management for agency owners
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, UserPlus, Search, Filter, RefreshCw, ArrowLeft,
  ChevronRight, Mail, Phone, Shield, AlertTriangle, CheckCircle,
  Clock, TrendingUp, TrendingDown, MoreVertical, Edit, Trash2,
  UserCog, Building2, Award, X
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Status badge component
function StatusBadge({ status }) {
  const configs = {
    active: { label: 'Active', bg: 'bg-green-100', text: 'text-green-700' },
    inactive: { label: 'Inactive', bg: 'bg-gray-100', text: 'text-gray-600' },
    pending: { label: 'Pending', bg: 'bg-yellow-100', text: 'text-yellow-700' }
  };
  const config = configs[status] || configs.inactive;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// Compliance status indicator
function ComplianceIndicator({ compliance }) {
  if (!compliance) {
    return (
      <div className="flex items-center gap-1 text-gray-400 text-sm">
        <Clock size={14} />
        <span>Not set up</span>
      </div>
    );
  }

  const hasEO = compliance.eo_expiration && new Date(compliance.eo_expiration) > new Date();
  const hasLicense = compliance.resident_license_expiration &&
    new Date(compliance.resident_license_expiration) > new Date();
  const hasAHIP = compliance.ahip_certified && compliance.ahip_expiration &&
    new Date(compliance.ahip_expiration) > new Date();

  const allGood = hasEO && hasLicense;
  const hasWarnings = !hasEO || !hasLicense;

  return (
    <div className={`flex items-center gap-1 text-sm ${allGood ? 'text-green-600' : 'text-orange-600'}`}>
      {allGood ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
      <span>{allGood ? 'Compliant' : 'Needs Attention'}</span>
    </div>
  );
}

// Agent Card Component
function AgentCard({ agent, onEdit, onView }) {
  const [showMenu, setShowMenu] = useState(false);

  const riskBreakdown = agent.riskBreakdown || {};
  const atRiskCount = (riskBreakdown.critical || 0) + (riskBreakdown.high || 0) + (riskBreakdown.elevated || 0);
  const retentionRate = agent.retentionRate || 0;

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#172A3A] to-[#20344A] flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
            {agent.name?.charAt(0) || '?'}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">{agent.name}</h3>
              <StatusBadge status={agent.status} />
            </div>
            <p className="text-sm text-gray-500 truncate">{agent.email}</p>
            {agent.phone && (
              <p className="text-sm text-gray-400">{agent.phone}</p>
            )}
          </div>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVertical size={18} className="text-gray-400" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 min-w-[150px]">
                <button
                  onClick={() => { setShowMenu(false); onView(agent); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <UserCog size={16} />
                  View Details
                </button>
                <button
                  onClick={() => { setShowMenu(false); onEdit(agent); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Edit size={16} />
                  Edit Agent
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{agent.totalClients || 0}</div>
            <div className="text-xs text-gray-500">Clients</div>
          </div>
          <div className={`rounded-lg p-3 text-center ${atRiskCount > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <div className={`text-xl font-bold ${atRiskCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {atRiskCount}
            </div>
            <div className="text-xs text-gray-500">At Risk</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-blue-600">
              {retentionRate > 0 ? `${retentionRate}%` : '--'}
            </div>
            <div className="text-xs text-gray-500">Retention</div>
          </div>
        </div>

        {/* Compliance Row */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <ComplianceIndicator compliance={agent.compliance} />
          <Link
            to={`/agency/agents/${agent.userId}`}
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            View Profile <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// Add/Edit Agent Modal
function AgentModal({ agent, onClose, onSave }) {
  const [formData, setFormData] = useState({
    email: agent?.email || '',
    role: agent?.role || 'agent',
    // Compliance fields
    npn: agent?.compliance?.npn || '',
    resident_state: agent?.compliance?.resident_state || '',
    resident_license_number: agent?.compliance?.resident_license_number || '',
    resident_license_expiration: agent?.compliance?.resident_license_expiration || '',
    eo_carrier: agent?.compliance?.eo_carrier || '',
    eo_policy_number: agent?.compliance?.eo_policy_number || '',
    eo_expiration: agent?.compliance?.eo_expiration || '',
    ahip_certified: agent?.compliance?.ahip_certified || false,
    ahip_expiration: agent?.compliance?.ahip_expiration || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const url = agent
        ? `${BASE}/agency/agents/${agent.userId}`
        : `${BASE}/agency/invite`;
      const method = agent ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to save agent');

      onSave(data.agent);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {agent ? 'Edit Agent' : 'Invite Agent'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Basic Information</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!agent}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          </div>

          {/* Compliance Info */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Compliance Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">NPN</label>
                <input
                  type="text"
                  value={formData.npn}
                  onChange={e => setFormData({ ...formData, npn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resident State</label>
                <input
                  type="text"
                  value={formData.resident_state}
                  onChange={e => setFormData({ ...formData, resident_state: e.target.value.toUpperCase() })}
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                <input
                  type="text"
                  value={formData.resident_license_number}
                  onChange={e => setFormData({ ...formData, resident_license_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Expiration</label>
                <input
                  type="date"
                  value={formData.resident_license_expiration}
                  onChange={e => setFormData({ ...formData, resident_license_expiration: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* E&O Insurance */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">E&O Insurance</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Carrier</label>
                <input
                  type="text"
                  value={formData.eo_carrier}
                  onChange={e => setFormData({ ...formData, eo_carrier: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                <input
                  type="text"
                  value={formData.eo_policy_number}
                  onChange={e => setFormData({ ...formData, eo_policy_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">E&O Expiration</label>
                <input
                  type="date"
                  value={formData.eo_expiration}
                  onChange={e => setFormData({ ...formData, eo_expiration: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* AHIP */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">AHIP Certification</h3>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ahip_certified}
                  onChange={e => setFormData({ ...formData, ahip_certified: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">AHIP Certified</span>
              </label>
              {formData.ahip_certified && (
                <input
                  type="date"
                  value={formData.ahip_expiration}
                  onChange={e => setFormData({ ...formData, ahip_expiration: e.target.value })}
                  placeholder="Expiration"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? 'Saving...' : agent ? 'Save Changes' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Agency Agents Page
export default function AgencyAgents() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  // Load agents
  useEffect(() => {
    async function loadAgents() {
      setLoading(true);
      try {
        const res = await fetch(`${BASE}/agency/agents`, { credentials: 'include' });
        if (res.status === 403) {
          navigate('/dashboard');
          return;
        }
        const data = await res.json();
        if (data.ok) {
          setAgents(data.agents);
        }
      } catch (err) {
        console.error('Failed to load agents:', err);
      } finally {
        setLoading(false);
      }
    }
    loadAgents();
  }, [navigate]);

  // Filter agents
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = !searchQuery ||
      agent.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Sort by client count (most clients first)
  const sortedAgents = [...filteredAgents].sort((a, b) => (b.totalClients || 0) - (a.totalClients || 0));

  const handleAddAgent = () => {
    setEditingAgent(null);
    setShowModal(true);
  };

  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setShowModal(true);
  };

  const handleViewAgent = (agent) => {
    navigate(`/agency/agents/${agent.userId}`);
  };

  const handleSaveAgent = (savedAgent) => {
    if (editingAgent) {
      setAgents(prev => prev.map(a => a.userId === savedAgent.userId ? savedAgent : a));
    } else {
      setAgents(prev => [...prev, savedAgent]);
    }
    setShowModal(false);
    setEditingAgent(null);
  };

  // Calculate totals
  const totalClients = agents.reduce((sum, a) => sum + (a.totalClients || 0), 0);
  const totalAtRisk = agents.reduce((sum, a) => {
    const rb = a.riskBreakdown || {};
    return sum + (rb.critical || 0) + (rb.high || 0) + (rb.elevated || 0);
  }, 0);
  const activeAgents = agents.filter(a => a.status === 'active').length;

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Link
              to="/agency/dashboard"
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <Users size={28} className="text-[#172A3A]" />
              <h1 className="text-2xl sm:text-3xl font-bold text-[#172A3A]">
                Agent Management
              </h1>
            </div>
            <p className="text-gray-600 mt-1">
              Manage your agency's agents, track compliance, and monitor performance
            </p>
          </div>

          <button
            onClick={handleAddAgent}
            className="flex items-center gap-2 px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:opacity-90 transition-opacity self-start"
          >
            <UserPlus size={18} />
            Invite Agent
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-3xl font-bold text-[#172A3A]">{agents.length}</div>
            <div className="text-sm text-gray-500">Total Agents</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-3xl font-bold text-green-600">{activeAgents}</div>
            <div className="text-sm text-gray-500">Active</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{totalClients}</div>
            <div className="text-sm text-gray-500">Total Clients</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="text-3xl font-bold text-red-600">{totalAtRisk}</div>
            <div className="text-sm text-gray-500">Clients At Risk</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {/* Agent Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl shadow p-4 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="h-16 bg-gray-100 rounded-lg"></div>
                  <div className="h-16 bg-gray-100 rounded-lg"></div>
                  <div className="h-16 bg-gray-100 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        ) : sortedAgents.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No agents found' : 'No agents yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Invite agents to start managing your agency\'s book of business'
              }
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button
                onClick={handleAddAgent}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:opacity-90"
              >
                <UserPlus size={18} />
                Invite Your First Agent
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedAgents.map(agent => (
              <AgentCard
                key={agent.userId}
                agent={agent}
                onEdit={handleEditAgent}
                onView={handleViewAgent}
              />
            ))}
          </div>
        )}

        {/* Agent Modal */}
        {showModal && (
          <AgentModal
            agent={editingAgent}
            onClose={() => { setShowModal(false); setEditingAgent(null); }}
            onSave={handleSaveAgent}
          />
        )}
      </div>
    </div>
  );
}
